import type {EntityRiskRegisterEntry, MaturityDimension, ChangeAssessmentEntry} from "../../types";

export const ENTITY_RISK_REGISTER: EntityRiskRegisterEntry[] = [
  {
    id: "err-01",
    riskId: "BR-01",
    description: "Supply chain disruption affecting raw material availability and cost",
    category: "Operational",
    categoryColor: "blue",
    financialReportingImpact: "Inventory valuation, cost of sales accuracy, provision adequacy",
    linkedFsaIds: ["expenditures"],
    entityResponse:
      "Dual-sourcing strategy with quarterly supplier risk reviews; safety stock policy for critical materials",
    status: "mitigated",
  },
  {
    id: "err-02",
    riskId: "BR-02",
    description: "IFRS 17 adoption requiring significant changes to insurance contract accounting",
    category: "Regulatory",
    categoryColor: "purple",
    financialReportingImpact: "Insurance contract measurement, disclosure completeness, transition adjustments",
    linkedFsaIds: ["financial-reporting"],
    entityResponse: "Dedicated project team with external advisory support; parallel reporting commenced Q4 2025",
    status: "open",
  },
  {
    id: "err-03",
    riskId: "BR-03",
    description: "Cyber security incident compromising financial data integrity",
    category: "IT / Security",
    categoryColor: "red",
    financialReportingImpact: "Data integrity of financial records, business continuity, disclosure obligations",
    linkedFsaIds: ["financial-reporting"],
    entityResponse:
      "Security operations centre with 24/7 monitoring; annual penetration testing; cyber insurance coverage",
    status: "mitigated",
  },
  {
    id: "err-04",
    riskId: "BR-04",
    description: "Foreign exchange volatility on material cross-border revenue streams",
    category: "Financial",
    categoryColor: "green",
    financialReportingImpact: "Revenue translation, hedging effectiveness, fair value of FX derivatives",
    linkedFsaIds: ["revenue", "financial-reporting"],
    entityResponse: "Rolling 12-month hedging programme; monthly FX exposure reporting to CFO",
    status: "mitigated",
  },
  {
    id: "err-05",
    riskId: "BR-05",
    description: "Key person dependency in financial reporting function",
    category: "People",
    categoryColor: "orange",
    financialReportingImpact: "Continuity of financial close process, technical accounting judgement quality",
    linkedFsaIds: ["financial-reporting"],
    entityResponse: "Succession plans for Controller, Head of Tax, Treasury Manager; cross-training programme",
    status: "mitigated",
  },
  {
    id: "err-06",
    riskId: "BR-06",
    description: "Customer concentration risk — top 3 customers represent 42% of revenue",
    category: "Strategic",
    categoryColor: "teal",
    financialReportingImpact: "Revenue recognition, trade receivable impairment, going concern assessment",
    linkedFsaIds: ["revenue"],
    entityResponse:
      "Customer diversification strategy; quarterly concentration monitoring; enhanced credit assessments for top accounts",
    status: "open",
  },
];

export const MATURITY_DIMENSIONS: MaturityDimension[] = [
  {
    id: "mat-01",
    name: "Risk Identification",
    score: 3,
    rationale:
      "Formal risk register maintained with quarterly updates. Business unit risk champions feed into the central register. Covers strategic, operational, financial, and compliance risk categories.",
  },
  {
    id: "mat-02",
    name: "Risk Estimation",
    score: 2,
    rationale:
      "Qualitative risk assessment performed using likelihood/impact matrices, but no consistent quantitative scoring framework. Different business units apply varying scales, making aggregation and comparison difficult.",
  },
  {
    id: "mat-03",
    name: "Risk Response",
    score: 3,
    rationale:
      "Documented risk responses with assigned owners and target dates. Response options (mitigate, accept, transfer, avoid) are formally selected and recorded. Progress tracked at quarterly risk committee meetings.",
  },
  {
    id: "mat-04",
    name: "Change Responsiveness",
    score: 2,
    rationale:
      "Regulatory and compliance changes are tracked proactively through external monitoring services. However, operational and market changes (new products, system migrations, customer shifts) are identified reactively — often only captured after the change has been implemented.",
  },
  {
    id: "mat-05",
    name: "Documentation & Governance",
    score: 2,
    rationale:
      "Risk register exists and is reviewed by the risk committee. However, the register lacks formal version history and there is no documented risk appetite statement approved by the board. Risk reporting to the audit committee is included in the quarterly package but not as a standalone agenda item.",
  },
];

export const CHANGE_ASSESSMENT: ChangeAssessmentEntry[] = [
  {
    id: "ca-01",
    change: "New product launch — extended warranty programme (Q1 2026)",
    identified: true,
    assessed: true,
    responded: true,
    notes: "Revenue recognition implications assessed; new accounting policy drafted and approved by CFO.",
  },
  {
    id: "ca-02",
    change: "Warehouse management system migration to cloud-hosted WMS (Q4 2025)",
    identified: true,
    assessed: true,
    responded: false,
    notes: "Impact on inventory controls assessed, but updated control procedures not yet documented or tested.",
  },
  {
    id: "ca-03",
    change: "Loss of second-largest customer — contract not renewed (December 2025)",
    identified: true,
    assessed: false,
    responded: false,
    notes:
      "Revenue impact acknowledged but no formal assessment of financial reporting implications (impairment triggers, going concern).",
  },
  {
    id: "ca-04",
    change: "New ESG reporting regulation requiring climate-related disclosures (effective FY 2026)",
    identified: true,
    assessed: true,
    responded: true,
    notes: "Cross-functional working group established; disclosure requirements mapped to existing data sources.",
  },
  {
    id: "ca-05",
    change: "Acquisition of small logistics company (completed January 2026)",
    identified: true,
    assessed: true,
    responded: false,
    notes:
      "Purchase price allocation in progress. Integration of acquired entity's financial processes into group controls not yet complete.",
  },
];
